import * as types from './mutation-types'
import PracticeInsurance from '@/entities/PracticeInsurance'
import Reason from '@/entities/Reason'
import TimeBlocks from '@/entities/TimeBlocks'
import CreatedAppointment from '@/entities/CreatedAppointment'
import AppointmentType from '@/entities/AppointmentType'
import Vue from 'vue'
import { NO_INSURANCE_OPTION_ID, OTHER_INSURANCE_OPTION_ID } from '@/views/constants'

export default {
  [types.SAVE_ALL_DOC_LOCS] (state, docLocs) {
    state.allDocLocs = {
      ...docLocs
    }
  },
  [types.SAVE_INIT_CALENDARS_INFO] (state, initCalendarsInfo) {
    state.initCalendarsInfo = [
      ...initCalendarsInfo
    ]
  },
  [types.SAVE_CALENDARS] (state, calendars) {
    state.calendars = {
      ...calendars
    }
  },
  [types.SAVE_LOCATIONS] (state, locations) {
    state.allLocations = [
      ...locations
    ]
  },
  [types.SAVE_PROVIDERS] (state, providers) {
    state.allProviders = [
      ...providers
    ]
  },
  [types.SAVE_CALENDAR_INDEX_TO_LOCATION_AND_PROVIDER_MAP] (state, calendarIndexToLocationAndProviderMap) {
    state.calendarIndexToLocationAndProviderMap = calendarIndexToLocationAndProviderMap
  },
  [types.SAVE_CALENDAR_ID_TO_INDEX_MAP] (state, calendarIdToIndexMap) {
    state.calendarIdToIndexMap = calendarIdToIndexMap
  },
  [types.UPDATE_INSURANCES] (state, { allDocsIns, allDocs = [] }) {
    const updatedInsurances = {}
    Object.keys(allDocsIns).forEach(providerId => {
      allDocsIns[providerId].forEach(insurance => {
        const existInsurance = state.insurances[insurance.insurance_provider_id] || updatedInsurances[insurance.insurance_provider_id]
        let itemForSaving
        if (!existInsurance) {
          itemForSaving = new PracticeInsurance(insurance, allDocs)
        } else {
          itemForSaving = {
            ...existInsurance
          }
          // if insurance has specific doctor_id, and saved variant doesn't have it
          if (!itemForSaving.providerIds.includes(providerId)) {
            // new doctor_id value added to providerIds list that supports this insurance
            itemForSaving.providerIds = [
              ...itemForSaving.providerIds,
              +providerId // providerId should be a number
            ]
          }
        }
        updatedInsurances[insurance.insurance_provider_id] = itemForSaving
      })
    })
    state.insurances = {
      ...state.insurances,
      ...updatedInsurances
    }
  },
  [types.UPDATE_INSURANCES_WITH_CALENDAR_INDEX] (state, { insurancesIdsList = [], calendarIndex } = {}) {
    const updatesIns = {}
    if (!insurancesIdsList.length > 0) {
      return
    }
    for (const id in state.insurances) {
      updatesIns[id] = state.insurances[id]

      if (insurancesIdsList.includes(+id) && !updatesIns[id].calendarIndexes.includes(calendarIndex)) {
        updatesIns[id].calendarIndexes.push(calendarIndex)
      }
    }

    state.insurances = {
      ...updatesIns
    }
  },
  [types.UPDATE_INSURANCES_FOR_PRACTICES_WITH_ONE_PROVIDER] (state, { insurances, allDocs }) {
    const updatedInsurances = {}
    insurances.forEach(ins => {
      // skip no/other options
      if ([NO_INSURANCE_OPTION_ID, OTHER_INSURANCE_OPTION_ID].includes(ins.id)) {
        return
      }
      const existedInsItem = state.insurances[ins.id]
      updatedInsurances[ins.id] = existedInsItem || new PracticeInsurance(ins, allDocs, true)
    })
    state.insurances = {
      ...state.insurances,
      ...updatedInsurances
    }
  },
  [types.UPDATE_REASONS] (state, reasons) {
    state.reasons = {
      ...state.reasons,
      ...reasons.reduce((reasons, reason) => {
        reasons[reason.id] = new Reason(reason)
        return reasons
      }, {})
    }
  },
  [types.SET_APPOINTMENT_TYPES] (state, appointmentTypes) {
    state.appointmentTypes = [
      ...appointmentTypes.map(apptType => new AppointmentType(apptType))
    ]
  },
  [types.SET_APPOINTMENT_TYPES_ENABLED] (state, enabled) {
    state.appointmentTypesEnabled = enabled
  },
  [types.REMOVE_CALENDAR] (state, calendarIndex) {
    const calendarsCopy = {
      ...state.calendars
    }
    delete calendarsCopy[calendarIndex]
    // full obj replacing used to initialise all recalculations
    state.calendars = calendarsCopy
  },
  [types.UPDATE_TIME_BLOCKS] (state, { calendarIndex, timeBlocks }) {
    const calendar = state.calendars[calendarIndex]
    calendar && (calendar.timeBlocks = new TimeBlocks(timeBlocks))
  },
  [types.UPDATE_CALENDAR_PHONE_NUMBERS] (state, { calendarIndex, phone, formattedPhone }) {
    const calendar = state.calendars[calendarIndex]
    Vue.set(calendar, 'displayInfo', {
      ...calendar.displayInfo,
      phoneFormatted: formattedPhone,
      phone: phone
    })
  },
  [types.UPDATE_CALENDAR_INSURANCES] (state, { calendarIndex, insurances }) {
    const calendar = state.calendars[calendarIndex]
    calendar.additionalData.insurances = insurances.map((ins) => {
      return new PracticeInsurance(ins)
    })
  },
  [types.UPDATE_CALENDAR_REASONS] (state, { calendarIndex, reasons }) {
    const calendar = state.calendars[calendarIndex]
    calendar.additionalData.reasons = reasons.map(reason => new Reason(reason))
  },
  [types.UPDATE_CREATED_APPOINTMENT] (state, createdAppointment) {
    state.createdAppointment = new CreatedAppointment(createdAppointment)
  },
  [types.RESET_CREATED_APPOINTMENT] (state) {
    state.createdAppointment = null
  },
  [types.ADD_ITEM_TO_A_QUEUE] (state, calendarIndex) {
    const curIndex = state.requestQueue.length
    state.requestQueue.push({
      calendarIndex: calendarIndex,
      id: curIndex,
      prevItemIndex: curIndex ? curIndex - 1 : null,
      processed: false,
      nextItemIndex: curIndex + 1,
      precessCallback: null
    })
  },
  [types.SET_RESPONSE_CALLBACK_TO_ITEM_IN_A_QUEUE] (state, { calendarIndex, precessCallback }) {
    const queueItem = state.requestQueue.find(el => {
      return el.calendarIndex === calendarIndex
    })
    if (queueItem) {
      queueItem.precessCallback = precessCallback
    }
  },
  [types.SET_QUEUE_ITEM_IS_PROCESSED] (state, calendarIndex) {
    const queueItem = state.requestQueue.find(el => {
      return el.calendarIndex === calendarIndex
    })
    queueItem.processed = true
  },
  [types.CLEAR_REQUEST_QUEUE] (state) {
    state.requestQueue = []
  },
  [types.SAVE_CALENDAR_ID] (state, id) {
    state.savedCalendarsIds.add(id)
  },
  [types.CLEAR_SAVED_CALENDARS_IDS_LIST] (state) {
    state.savedCalendarsIds = new Set()
  },
  [types.CLEAR_CALENDARS] (state) {
    state.calendars = {}
  },
  [types.SET_GOOGLE_MAPS_API_KEY] (state, googleMapsApiKey) {
    state.googleMapsApiKey = googleMapsApiKey
  }
}
